import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";
import CreateCoursePopup from "../../common/createPopup";
import {
  getSubjectsByCourseKey,
  updateSubject,
  deleteSubject,
  addSubject,
} from "../../../helper/apicalls/course";
import ActionButtons from "../../common/ActionButtons";
import "./../../../styles/admin/subjects.scss";
import GlobalLoader from "../../common/GlobalLoader";
import EmptyPage from "../../common/emptyPage";

const CourseYearList = ({
  hideBreadcrumbs = false,
  cacheSubjectData = [],
  setCacheSubjectData,
  showActionBtn,
  role,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { key } = useParams();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [popupHeaderTitle, setPopupHeaderTitle] = useState("");
  const [popupBtnText, setPopupBtnText] = useState("");
  const [popupInitialValue, setPopupInitialValue] = useState("");
  const [selectedSubject, setSelectedSubject] = useState(null);

  const isEvalPath = location.pathname.startsWith("/admin/eval");

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const data = await getSubjectsByCourseKey(key);

      if (Array.isArray(data) && data.length > 0) {
       
        const extractedSubjects = data.map((item) => ({
          id: item.id,
          name: item.name,
          key: item.key,
          total_number_quizes: item.total_number_quizes,
          created_at: item.created_at,
          updated_at: item.updated_at,
        }));

        setSubjects(extractedSubjects);
        setCacheSubjectData(extractedSubjects);
      } else if (data && typeof data === "object" && data.course) {
       
        const subject = {
          id: data.id,
          name: data.name,
          key: data.key,
          total_number_quizes: data.total_number_quizes,
          created_at: data.created_at,
          updated_at: data.updated_at,
        };

        setSubjects([subject]);
      } else {
        setSubjects([]);
      }
    } catch (err) {
      console.error("Error fetching subjects:", err);
      setSubjects([
        { id: "691ebe56468548226c5b3ae6", name: "subject 1", key: "TE1-SU1" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteButtonClick = (subject) => {
    setIsConfirmDeleteOpen(true);
    setSelectedSubject(subject);
  };

  const handleDelete = async (event) => {
    event.stopPropagation();
    try {
      await deleteSubject(selectedSubject.key);
      fetchSubjects();
      setIsConfirmDeleteOpen(false);
    } catch (err) {
      console.error("Error deleting subject:", err);
    }
  };

  const handleEdit = (subject) => {
    setSelectedSubject(subject);
    setPopupHeaderTitle(`Edit ${subject.name}`);
    setPopupBtnText("Update");
    setPopupInitialValue(subject.name);
    setOpenPopup(true);
  };

  const handleUpdate = async (updatedName, totalNumberQuizes) => {
    try {
      const updatedSubject = {
        ...selectedSubject,
        name: updatedName,
        total_number_quizes: totalNumberQuizes ? parseInt(totalNumberQuizes, 10) : 0,
      };

      await updateSubject(updatedSubject);
      fetchSubjects();
    } catch (err) {
      console.error("Error updating subject:", err);
    }
    setOpenPopup(false);
    setPopupInitialValue("");
    setSelectedSubject(null);
  };

  const handleAddSubject = async (subjectName, totalNumberQuizes) => {
    try {
      const newSubject = {
        name: subjectName,
        course_key: key,
        total_number_quizes: totalNumberQuizes ? parseInt(totalNumberQuizes, 10) : 0,
      };

      await addSubject(newSubject);
      fetchSubjects();
    } catch (err) {
      console.error("Error adding subject:", err);
    }
    setOpenPopup(false);
    setPopupInitialValue("");
    setSelectedSubject(null);
  };

  const handleSubjectClick = (subject) => {
    if (role === "admin") {
      if (isEvalPath) {
        navigate(`/admin/eval/${key}/subject/${subject.key}`);
      } else {
        navigate(`/admin/course/${key}/subject/${subject.key}`);
      }
    } else {
      navigate(`/institution/course/${key}/subject/${subject.key}`);
    }
  };

  const handleClosePopup = () => {
    setOpenPopup(false);
    setPopupInitialValue("");
    setSelectedSubject(null);
  };

  useEffect(() => {
    if (cacheSubjectData?.length === 0) {
      fetchSubjects();
    } else {
      setSubjects(cacheSubjectData);
      setLoading(false);
    }
  }, [key, location]);

  return (
    <>
      {loading && <GlobalLoader visible={loading} text="Loading..." />}
      {subjects?.length > 0 ? (
        <div className="subjects-container">
          <div className="subjects-card">
            <div className="subjects-card-header">
              <span>Name of the subjects</span>
              {showActionBtn && !isEvalPath && (
                <span style={{ marginRight: "16px" }}>Actions</span>
              )}
            </div>
            <ul className="subjects-list">
              {subjects.map((sub) => (
                <li key={sub.id} className="subject-item">
                  <span
                    className="subject-name"
                    onClick={() => handleSubjectClick(sub)}
                  >
                    {sub.name}
                  </span>
                  {showActionBtn && !isEvalPath && (
                    <div className="subject-actions">
                      <span
                        className="action-link"
                        onClick={() => handleEdit(sub)}
                      >
                        Edit
                      </span>
                      <span
                        className="action-link"
                        onClick={() => handleDeleteButtonClick(sub)}
                      >
                        Delete
                      </span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            {showActionBtn && (
              <div className="subjects-card-footer">
                <ActionButtons
                  context="subject"
                  courseKey={key}
                  onCreateSuccess={fetchSubjects}
                  buttonLabel="Add Subject"
                />
              </div>
            )}
          </div>
          {isConfirmDeleteOpen && (
            <DeleteConfirmPopUp
              isConfirmDeleteOpen={isConfirmDeleteOpen}
              setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
              handleDelete={handleDelete}
            />
          )}
          {openPopup && (
            <CreateCoursePopup
              open={openPopup}
              handleClose={handleClosePopup}
              title="Subject"
              headerTitle={popupHeaderTitle}
              buttonText={popupBtnText}
              initialValue={popupInitialValue}
              initialTotalNumberQuizes={selectedSubject ? selectedSubject.total_number_quizes : ""}
              onSave={selectedSubject ? handleUpdate : handleAddSubject}
            />
          )}
        </div>
      ) : (
        <>
          <EmptyPage content={"No subjects Available"} />
          {!isEvalPath && (
            <div className="subjects-card-footer">
              <ActionButtons
                context="subject"
                courseKey={key}
                onCreateSuccess={fetchSubjects}
                buttonLabel="Add Subject"
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

export default CourseYearList;
